import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import JsonLd from '../../components/JsonLd/JsonLd';
import MainNav from '../../components/MainNav/MainNav';
import Footer from '../../components/Footer/Footer';
import { getActiveJobOffers } from '../../data/jobOffers';
import './EmpleoPage.css';

const FORMSPREE_ID = process.env.REACT_APP_FORMSPREE_ID;
const EMPLEO_WEBHOOK_URL = process.env.REACT_APP_HABLEMOS_WEBHOOK_URL;
const CANONICAL = 'https://e-asy.es/empleo';

const buildJobPostingSchema = (job) => ({
  '@context': 'https://schema.org',
  '@type': 'JobPosting',
  title: job.title,
  description: [
    job.intro.join(' '),
    'Funciones: ' + job.responsibilities.join(' '),
    'Requisitos: ' + job.requirements.join(' '),
  ].join(' '),
  identifier: {
    '@type': 'PropertyValue',
    name: 'E-ASY',
    value: job.id,
  },
  datePosted: '2026-07-09',
  validThrough: '2026-10-09',
  employmentType: 'FULL_TIME',
  hiringOrganization: {
    '@type': 'Organization',
    name: 'E-ASY',
    sameAs: 'https://e-asy.es',
    logo: 'https://e-asy.es/logo512.png?v=20260709',
  },
  jobLocation: {
    '@type': 'Place',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Santa Cruz de Tenerife',
      addressRegion: 'Canarias',
      addressCountry: 'ES',
    },
  },
  applicantLocationRequirements: {
    '@type': 'Country',
    name: 'España',
  },
});

const JobOfferCard = ({ job }) => {
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    message: '',
    cv: null,
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, cv: e.target.files?.[0] ?? null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    setLoading(true);

    const fullName = `${formData.firstname} ${formData.lastname}`.trim();
    const bodyText = [
      `Puesto: ${job.title}`,
      `Nombre: ${fullName}`,
      `Email: ${formData.email}`,
      formData.phone ? `Teléfono: ${formData.phone}` : null,
      formData.cv ? `CV (archivo seleccionado): ${formData.cv.name}` : null,
      '',
      formData.message || '(Sin mensaje adicional)',
    ]
      .filter(Boolean)
      .join('\n');

    try {
      if (FORMSPREE_ID) {
        const payload = new FormData();
        payload.append('nombre', formData.firstname);
        payload.append('apellidos', formData.lastname);
        payload.append('email', formData.email);
        payload.append('teléfono', formData.phone);
        payload.append('mensaje', bodyText);
        payload.append('_subject', job.applicationSubject);
        if (formData.cv) {
          payload.append('cv', formData.cv);
        }

        const res = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
          method: 'POST',
          body: payload,
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error('Error al enviar');
      } else if (EMPLEO_WEBHOOK_URL) {
        const res = await fetch(EMPLEO_WEBHOOK_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: job.applicationEmail,
            cc: '',
            cc1: '',
            cc2: '',
            cc3: '',
            subject: job.applicationSubject,
            text: bodyText,
          }),
        });
        if (!res.ok) throw new Error('Error al enviar');
      } else {
        throw new Error('Formulario no configurado');
      }

      setStatus('success');
      setFormData({
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
        message: '',
        cv: null,
      });
      e.target.reset();
    } catch {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <article className="job-card" id={job.slug}>
      <header className="job-card__header">
        <span className="job-card__icon" aria-hidden="true">
          {job.icon}
        </span>
        <div className="job-card__title-group">
          <h3 className="job-card__title">{job.title}</h3>
          <div className="job-card__tags">
            <span className="tag tag--contract">{job.contract}</span>
            <span className="tag tag--location">{job.location}</span>
            <span className="tag tag--modality">{job.modality}</span>
          </div>
        </div>
      </header>

      <div className="job-card__body">
        <div className="job-card__intro">
          {job.intro.map((paragraph) => (
            <p key={paragraph.slice(0, 40)}>{paragraph}</p>
          ))}
          {job.highlight && <p className="highlight-text">{job.highlight}</p>}
        </div>

        <section className="job-section">
          <h4>Qué harás</h4>
          <ul>
            {job.responsibilities.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="job-section">
          <h4>Qué buscamos</h4>
          <ul>
            {job.requirements.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        {job.niceToHave?.length > 0 && (
          <section className="job-section">
            <h4>Valoramos además</h4>
            <ul className="nested-list">
              {job.niceToHave.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>
        )}

        <section className="job-section">
          <h4>Qué ofrecemos</h4>
          <ul className="benefits-list">
            {job.benefits.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      </div>

      <section className="application-section">
        <h3 className="section-title">Enviar candidatura</h3>
        <p className="application-intro">
          Completa el formulario con tus datos. Si quieres, indica el nombre de tu CV en el
          campo correspondiente o incluye un enlace a LinkedIn en el mensaje.
        </p>

        {status === 'success' && (
          <div className="form-status success" role="status">
            Candidatura enviada correctamente. Te contactaremos si encaja con el perfil.
          </div>
        )}
        {status === 'error' && (
          <div className="form-status error" role="alert">
            No se pudo enviar la candidatura. Escríbenos a {job.applicationEmail}.
          </div>
        )}

        <form className="application-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor={`${job.id}-firstname`}>Nombre *</label>
              <input
                id={`${job.id}-firstname`}
                name="firstname"
                type="text"
                required
                value={formData.firstname}
                onChange={handleChange}
                placeholder="Tu nombre"
              />
            </div>
            <div className="form-group">
              <label htmlFor={`${job.id}-lastname`}>Apellidos *</label>
              <input
                id={`${job.id}-lastname`}
                name="lastname"
                type="text"
                required
                value={formData.lastname}
                onChange={handleChange}
                placeholder="Tus apellidos"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor={`${job.id}-email`}>Email *</label>
              <input
                id={`${job.id}-email`}
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="tu@email.com"
              />
            </div>
            <div className="form-group">
              <label htmlFor={`${job.id}-phone`}>Teléfono</label>
              <input
                id={`${job.id}-phone`}
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+34 600 000 000"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor={`${job.id}-message`}>Carta de presentación / mensaje</label>
            <textarea
              id={`${job.id}-message`}
              name="message"
              rows={5}
              value={formData.message}
              onChange={handleChange}
              placeholder="Cuéntanos brevemente tu experiencia en marketing B2B y por qué te interesa E-ASY."
            />
          </div>

          <div className="form-group">
            <label htmlFor={`${job.id}-cv`}>Currículum (PDF, opcional)</label>
            <div className="file-upload-wrapper">
              <input
                id={`${job.id}-cv`}
                name="cv"
                type="file"
                accept=".pdf,.doc,.docx"
                className="file-input"
                onChange={handleFileChange}
                required={false}
              />
              <div className="file-upload-label">
                <span className="file-icon">📎</span>
                <span className="file-text">
                  {formData.cv ? formData.cv.name : 'Arrastra tu CV o haz clic para seleccionar'}
                </span>
              </div>
            </div>
          </div>

          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? 'Enviando…' : 'Enviar candidatura'}
          </button>

          <p className="form-privacy">
            Al enviar aceptas nuestra{' '}
            <Link to="/politica-privacidad">política de privacidad</Link>.
          </p>
        </form>
      </section>
    </article>
  );
};

const EmpleoPage = ({ darkMode, toggleDarkMode }) => {
  const activeJobs = getActiveJobOffers();

  const jobPostingSchemas = activeJobs.map(buildJobPostingSchema);

  return (
    <>
      <SEO
        title={
          activeJobs.length > 0
            ? `${activeJobs[0].title} y más ofertas | Trabaja en E-ASY`
            : 'Trabaja con nosotros | Ofertas de empleo | E-ASY'
        }
        description={
          activeJobs.length > 0
            ? `Oferta de empleo: ${activeJobs[0].title} en E-ASY (Tenerife). Consultoría tecnológica, ERP, automatización y Kit Digital. Envía tu candidatura.`
            : 'Únete al equipo de E-ASY. Buscamos talento apasionado por la tecnología. Consulta nuestras ofertas de empleo y envía tu candidatura.'
        }
        keywords="empleo tecnología, trabajo IT Canarias, ofertas empleo Tenerife, marketing B2B, responsable marketing, trabajo consultoría tecnológica"
        canonical={CANONICAL}
      />

      {jobPostingSchemas.map((schema, index) => (
        <JsonLd key={activeJobs[index].id} id={`jsonld-job-${activeJobs[index].id}`} data={schema} />
      ))}

      <MainNav darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className={`empleo-page ${darkMode ? 'dark' : 'light'}`}>
        <section className="empleo-hero">
          <div className="empleo-hero__content">
            <h1>Únete a E-ASY</h1>
            <p>
              Buscamos personas apasionadas por la tecnología y el marketing B2B que quieran
              crecer en un entorno innovador, con proyectos reales y autonomía para aportar.
            </p>
          </div>
        </section>

        <section className="empleo-ofertas">
          <div className="empleo-container">
            <h2 className="section-title">Ofertas activas</h2>

            {activeJobs.length > 0 ? (
              activeJobs.map((job) => (
                <JobOfferCard key={job.id} job={job} />
              ))
            ) : (
              <div className="no-offers-card">
                <div className="no-offers-icon">📭</div>
                <h3 className="no-offers-title">No hay ofertas activas en este momento</h3>
                <p className="no-offers-text">
                  Actualmente no tenemos vacantes abiertas, pero estamos en constante crecimiento.
                  Te invitamos a seguirnos en LinkedIn para estar al tanto de futuras
                  oportunidades.
                </p>
                <div className="no-offers-actions">
                  <a
                    href="https://www.linkedin.com/company/insideeasy/"
                    className="linkedin-button"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="linkedin-icon">in</span>
                    Síguenos en LinkedIn
                  </a>
                  <Link to="/contacto" className="contact-button">
                    Contactar con nosotros
                  </Link>
                </div>
              </div>
            )}

            <div className="company-values">
              <h3 className="values-title">¿Por qué trabajar en E-ASY?</h3>
              <div className="values-grid">
                <div className="value-item">
                  <span className="value-icon">🚀</span>
                  <h4>Crecimiento profesional</h4>
                  <p>Formación continua y participación en proyectos con impacto real.</p>
                </div>
                <div className="value-item">
                  <span className="value-icon">🤝</span>
                  <h4>Equipo colaborativo</h4>
                  <p>Ambiente de trabajo profesional donde cada persona aporta valor.</p>
                </div>
                <div className="value-item">
                  <span className="value-icon">💡</span>
                  <h4>Innovación constante</h4>
                  <p>Trabajamos con las últimas tecnologías: IA, ERP, Cloud y más.</p>
                </div>
                <div className="value-item">
                  <span className="value-icon">🏝️</span>
                  <h4>Desde Canarias al mundo</h4>
                  <p>Proyectos nacionales e internacionales desde un entorno privilegiado.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer darkMode={darkMode} />
    </>
  );
};

export default EmpleoPage;
